/**
 * Sharing a post — or an interest ("seeking a consortium / partners" on a call; INTEREST prop) — in its actions
 * row: the share menu (copy its link, the device's share sheet, email, a message on Otable) and the repost button
 * (repost, or repost with your own words).
 * Backend: POST /interact/posts/ {repost_of | repost_comment, body} — only public posts (and interests) can be reposted.
 */
import React, { useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ListItemIcon,
  ListItemText, Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Email as EmailIcon, IosShare as ShareIcon, Repeat as RepostIcon, EditNote as QuoteIcon,
  Send as MessageIcon,
} from '@mui/icons-material';
import { useUserContext } from 'shared/contexts/UserContext';
import { UserPicker } from 'components/collab/lab/LabDialogs';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { POST_MAX_LENGTH } from 'shared/helpers/postText';
import RepostedOriginal from './RepostedOriginal';
import { useMentions } from 'components/social/feed/mentions';

const authorIdOf = (post) => post.author_info?.id || post.author?.id || post.author;

/** The address of a post's own page (an interest's: its call's page; a public post's: its open page, no account). */
export const postUrl = (post, interest = false) => (interest
  ? `${window.location.origin}/funding/call/${post.target_id}/`
  : post.visibility === 'public'
    ? `${window.location.origin}/p/post/${post.id}/`
    : `${window.location.origin}/feed/${authorIdOf(post)}/posts/${post.id}/`);

const authorNameOf = (item) => item.author_name
  || [item.author_info?.first_name, item.author_info?.last_name].filter(Boolean).join(' ') || 'someone';

/** An interest as a repost shows it (the shape the server sends as a repost's `original`). */
export const interestAsOriginal = (c) => ({
  kind: 'interest', id: c.id, author: c.author_info || {}, author_name: authorNameOf(c), body: c.body,
  type_of_interest: c.type_of_interest, created: c.created, call_id: c.target_id, call_title: c.target_name || '',
  visibility: 'otable',          // interests are for Otable users only
});

const excerpt = (post) => {
  const text = (post.title || post.body || post.original?.body || '').replace(/\s+/g, ' ').trim();
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
};

/** Count a share or an action taken from the post (or interest), for its author's stats (never blocks the user). */
export const recordPostEvent = (post, kind, interest = false) => {
  axiosService.post(`/interact/${interest ? 'comments' : 'posts'}/${post.id}/events/`, { kind }).catch(() => {});
};

/** "Send in a message": the post's link, with a note, to people on Otable, as a message from you to each. */
function ShareInMessageDialog({ open, onClose, post, interest, url, what, name, onSent }) {
  const { user: me } = useUserContext() || {};
  const [people, setPeople] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const shared = `${what} by ${name}${excerpt(post) ? `: "${excerpt(post)}"` : ''}\n${url}`;

  const add = (u) => { if (u && u.id !== me?.id && !people.some((p) => p.id === u.id)) setPeople([...people, u]); };
  const close = () => { setPeople([]); setNote(''); setError(null); onClose(); };

  const send = async () => {
    setBusy(true);
    setError(null);
    const body = note.trim() ? `${note.trim()}\n\n${shared}` : shared;
    const failed = [];                               // [{ person, why }]: kept in the dialog to try again
    for (const person of people) {
      try {
        await axiosService.post('/interact/messages/', {
          sender_actor_type: 'user', sender_entity_id: me?.id,
          recipient_actor_type: 'user', recipient_entity_id: person.id, body,
        });
      } catch (err) {
        failed.push({ person, why: apiErrorMessage(err, 'not sent') });
      }
    }
    setBusy(false);
    if (failed.length) {
      setError(`Not sent to ${failed.map((f) => `${f.person.name} (${f.why})`).join(', ')}.`);
      setPeople(failed.map((f) => f.person));
      if (failed.length < people.length) onSent(people.length - failed.length);
      return;
    }
    onSent(people.length);
    close();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle>Send in a message</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <UserPicker label="To" value={null} onChange={add} />
          {people.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {people.map((p) => (
                <Chip key={p.id} label={p.name} avatar={<Avatar src={p.avatar_url || undefined}>{(p.name || '?')[0]}</Avatar>}
                  onDelete={() => setPeople(people.filter((x) => x.id !== p.id))} />
              ))}
            </Box>
          )}
          <TextField label="Your note (optional)" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2}
            fullWidth />
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>They receive</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {note.trim() ? `${note.trim()}\n\n${shared}` : shared}
            </Typography>
          </Box>
          {!interest && post.visibility && post.visibility !== 'public' && post.visibility !== 'otable' && (
            <Typography variant="caption" color="text.secondary">
              {post.visibility === 'private' ? 'Only you can open this post: others will see the message, not the post.'
                : 'Only your connections can open this post.'}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" startIcon={<MessageIcon />} onClick={send} disabled={busy || people.length === 0}>
          {busy ? 'Sending…' : `Send${people.length > 1 ? ` to ${people.length}` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PostShareButton({ post, sx, withLabel = false, interest = false }) {
  const [anchor, setAnchor] = useState(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const { showSnackbar = () => {} } = useSnackbar() || {};
  const url = postUrl(post, interest);
  const name = authorNameOf(post);
  const what = interest ? 'Interest' : 'Post';
  const record = (kind) => recordPostEvent(post, kind, interest);
  const close = () => setAnchor(null);

  const copy = async () => {
    close();
    record('share_link');
    try {
      await navigator.clipboard.writeText(url);
      showSnackbar('Link copied', 'success');
    } catch {
      window.prompt('Copy the link to the post:', url);      // clipboard blocked (http, old browser)
    }
  };
  const nativeShare = async () => {
    close();
    try {
      await navigator.share({ title: `${what} by ${name} on Otable`, text: excerpt(post), url });
      record('share_native');
    } catch { /* cancelled */ }
  };

  return (
    <>
      {withLabel ? (
        <Button size="small" startIcon={<ShareIcon />} onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' }, ...sx }}>
          Share
        </Button>
      ) : (
        <Tooltip title="Share">
          <IconButton size="small" aria-label="Share the post" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }} sx={sx}>
            <ShareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close} onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={copy}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Copy link" />
        </MenuItem>
        {typeof navigator !== 'undefined' && navigator.share && (
          <MenuItem onClick={nativeShare}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Share…" />
          </MenuItem>
        )}
        <MenuItem onClick={() => { close(); setMessageOpen(true); }}>
          <ListItemIcon><MessageIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Send in a message" />
        </MenuItem>
        <MenuItem component="a" onClick={() => { record('share_email'); close(); }}
          href={`mailto:?subject=${encodeURIComponent(`${what} by ${name} on Otable`)}&body=${encodeURIComponent(`${excerpt(post)}\n\n${url}`)}`}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Send by email" />
        </MenuItem>
        {!interest && post.visibility && post.visibility !== 'public' && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 2, pt: 0.5, maxWidth: 240 }}>
            {post.visibility === 'private' ? 'Only you can open the link.'
              : post.visibility === 'connections' ? 'Only your connections can open the link.'
                : 'Only Otable users can open the link.'}
          </Typography>
        )}
      </Menu>
      <ShareInMessageDialog open={messageOpen} onClose={() => setMessageOpen(false)} post={post} interest={interest}
        url={url} what={what} name={name}
        onSent={(n) => { record('share_message'); showSnackbar(`Sent to ${n} ${n === 1 ? 'person' : 'people'}`, 'success'); }} />
    </>
  );
}

export function RepostButton({ post, onReposted, interest = false }) {
  const [anchor, setAnchor] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // "@…" in your words: the same list of people to mention as in the composer
  const inputRef = useRef(null);
  const mentions = useMentions(text, setText, inputRef);
  const { showSnackbar = () => {} } = useSnackbar() || {};
  // a plain repost shares its original; the server does the same. An interest is shared as itself.
  const target = interest ? post : (post.is_repost && !(post.body || '').trim() ? post.original : post);
  const targetVisibility = interest ? 'otable' : (target === post ? post.visibility : target?.visibility);
  const shared = interest ? { repost_comment: post.id }
    : target?.kind === 'interest' ? { repost_comment: target.id } : { repost_of: target?.id };
  const can = target && !target.unavailable && ['public', 'otable'].includes(targetVisibility);
  const count = (target === post ? post.repost_count : null) || 0;
  // "Reposted by Ayşe Demir, John Carter and 3 more"
  const names = target === post ? (post.reposted_by || []) : [];
  const repostedBy = names.length
    ? `Reposted by ${names.slice(0, 5).join(', ')}${count > 5 ? ` and ${count - Math.min(5, names.length)} more` : ''}`
    : '';

  const send = async (body = '') => {
    setBusy(true);
    try {
      const res = await axiosService.post('/interact/posts/', { ...shared, body: mentions.apply(body) });
      showSnackbar(body ? 'Reposted with your thoughts' : 'Reposted to your feed', 'success');
      setQuoteOpen(false);
      setText('');
      mentions.reset();
      window.dispatchEvent(new CustomEvent('postCreated', { detail: { post: res.data } }));
      onReposted?.(res.data);
    } catch (err) {
      showSnackbar(apiErrorMessage(err, 'Could not repost.'), 'error');
    } finally {
      setBusy(false);
      setAnchor(null);
    }
  };

  return (
    <>
      <Tooltip title={[repostedBy, can ? '' : 'Only posts shared with everyone on Otable can be reposted'].filter(Boolean).join(' · ')}>
        <span>
          <Button size="small" startIcon={<RepostIcon />} disabled={!can || busy}
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            Repost{count > 0 && ` (${count})`}
          </Button>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => send('')} disabled={busy}>
          <ListItemIcon><RepostIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Repost" />
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); setQuoteOpen(true); }}>
          <ListItemIcon><QuoteIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Repost with your thoughts" />
        </MenuItem>
      </Menu>
      <Dialog open={quoteOpen} onClose={() => !busy && setQuoteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Repost with your thoughts</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} value={text} inputRef={inputRef} {...mentions.field}
            placeholder="What do you think about it? Type @ to mention someone" inputProps={{ maxLength: POST_MAX_LENGTH }} sx={{ mt: 1 }} />
          {mentions.list}
          {/* the post as it will appear under your words: rendered, with its link card or video */}
          <RepostedOriginal original={interest ? interestAsOriginal(post) : target} linkable={false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={() => send(text.trim())} disabled={busy || !text.trim()}>Repost</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
